<template>
    <!-- justify-center items-center -->
    <footer class="w-full bg-[#131314] border-t-gray-800 border-t pb-20">
        <div
            class="container py-10 px-4 min-h-[469px] justify-center lg:justify-between items-center flex flex-col lg:px-0"
        >
            <div
                class="w-full flex lg:flex-row flex-col justify-between gap-x-10 justify-items-stretch content-center h-full pb-6"
            >
                <div
                    class="flex flex-col justify-center lg:justify-start lg:items-start items-center text-center lg:text-start space-y-6"
                >
                    <div>
                        <NuxtLinkLocale :to="{ path: localePath('/') }" class="lg:mb-4 mb-2 block">
                            <AppLogo class="lg:w-[185px] lg:m-0 mx-auto h-9" />
                        </NuxtLinkLocale>

                        <p class="text-body-sm leading-5 text-gray-600 max-w-[220px]">
                            {{ footerData.subtitle }}
                        </p>
                    </div>

                    <div class="flex items-center space-x-2 text-gray-500">
                        <Icon name="tabler:mail" class="w-6 h-6" />
                        <a href="mailto:support@aihomedesign.com" class="text-body-md">support@aihomedesign.com</a>
                    </div>

                    <div class="flex space-x-4 items-center">
                        <NuxtLinkLocale v-for="icon in footerData.social" :to="icon.link" :aria-label="icon.ariaLabel">
                            <Icon class="w-6 h-6 text-gray-100" :name="icon.icon_src" />
                        </NuxtLinkLocale>
                    </div>
                    <ClientOnly>
                        <template #default>
                            <AppLanguageSelect />
                        </template>

                        <template #fallback>
                            <div class="w-full h-14 rounded-xl bg-gray-800 animate-pulse"></div>
                        </template>
                    </ClientOnly>
                </div>

                <div v-if="useDevice().isDesktop" v-for="item in footerData.realEstateHub">
                    <p class="text-body-lg font-bold text-gray-100 leading-6 mb-4">
                        {{ item.title }}
                    </p>
                    <ul class="flex flex-col space-y-6 text-gray-500">
                        <li v-for="link in item.content" class="text-body-md leading-5">
                            <NuxtLinkLocale @click="scrollToTopInSameUrl(link.link)" :to="link.link">
                                {{ link.text }}
                            </NuxtLinkLocale>
                        </li>
                    </ul>
                </div>

                <ClientOnly>
                    <AppAccordionList v-if="useDevice().isMobileOrTablet" :items="footerData.realEstateHub" />
                </ClientOnly>

                <ClientOnly>
                    <AppAccordionList v-if="useDevice().isMobileOrTablet" :items="footerData.furnishing" />
                </ClientOnly>
                <div v-if="useDevice().isDesktop" v-for="item in footerData.furnishing">
                    <p class="text-body-lg font-bold text-gray-100 leading-6 mb-4">{{ item.title }}</p>
                    <ul class="flex flex-col space-y-6 text-gray-500">
                        <li v-for="link in item.content" class="text-body-md leading-5 cursor-pointer">
                            <NuxtLinkLocale @click="scrollToTopInSameUrl(link.link)" :to="localePath(link.link)">
                                {{ link.text }}
                            </NuxtLinkLocale>
                        </li>
                    </ul>
                </div>

                <div v-if="useDevice().isDesktop" v-for="item in footerData.renovation">
                    <p class="text-body-lg font-bold text-gray-100 leading-6 mb-4">
                        {{ item.title }}
                    </p>
                    <ul class="flex flex-col space-y-6 text-gray-500">
                        <li v-for="link in item.content" class="text-body-md leading-5">
                            <NuxtLinkLocale @click="scrollToTopInSameUrl(link.link)" :to="link.link">
                                {{ link.text }}
                            </NuxtLinkLocale>
                        </li>
                    </ul>
                </div>

                <ClientOnly>
                    <AppAccordionList v-if="useDevice().isMobileOrTablet" :items="footerData.renovation" />
                </ClientOnly>

                <!-- <ClientOnly>
                    <AppAccordionList v-if="useDevice().isMobileOrTablet" :items="footerData.b2b" />
                </ClientOnly> -->

                <div v-if="useDevice().isDesktop" v-for="item in footerData.company">
                    <p class="text-body-lg font-bold text-gray-100 leading-6 mb-4">{{ item.title }}</p>
                    <ul class="flex flex-col space-y-6 text-gray-500">
                        <li v-for="link in item.content" class="text-body-md leading-5">
                            <NuxtLinkLocale @click="scrollToTopInSameUrl(link.link)" :to="link.link">
                                {{ link.text }}
                            </NuxtLinkLocale>

                            <!-- <span
                                v-if="link.badge"
                                class="bg-orange-400 ml-2 text-white rounded-full text-body-sm px-2 py-1"
                            >
                                {{ link.badge }}
                            </span> -->
                        </li>
                    </ul>
                </div>

                <ClientOnly>
                    <AppAccordionList
                        class="space-y-20"
                        v-if="useDevice().isMobileOrTablet"
                        :items="footerData.company"
                    />
                </ClientOnly>
            </div>

            <!-- * NAR image -->
            <div class="lg:py-6 py-16 border-t border-gray-800 w-full">
                <NuxtLink
                    external
                    target="_blank"
                    class="block w-fit lg:m-0 mx-auto"
                    to="https://www.nar.realtor/blogs/styled-staged-sold/generative-ai-is-your-ally-for-smart-staging-faster-deals
"
                >
                    <img
                        loading="lazy"
                        class=""
                        width="161"
                        height="72"
                        src="/images/footer/nar.webp"
                        alt="National association of realtors"
                    />
                </NuxtLink>
            </div>

            <div
                class="container w-full flex lg:flex-row flex-col h-full border-t border-gray-800 space-y-5 lg:space-y-0 lg:justify-between pt-6 text-body-sm font-medium text-gray-700"
            >
                <ul class="flex justify-between lg:space-x-6">
                    <li v-for="item in footerData.bottomLinks.links">
                        <NuxtLinkLocale :to="item.link">
                            {{ item.text }}
                        </NuxtLinkLocale>
                    </li>
                </ul>
                <p class="flex justify-center">{{ footerData.bottomLinks.copyRight }}</p>
            </div>
        </div>
    </footer>
</template>

<script setup lang="ts">
import { useFooter } from "~/data/footer";
const { footerData } = useFooter();
const localePath = useLocalePath();

const languages = [
    { id: 1, name: "English (US)", value: "en", desc: "(English US)", currency: "USD" },
    { id: 6, name: "English (UK)", value: "en-gb", desc: "(English UK)", currency: "GBP" },
    { id: 5, name: "French", value: "fr", desc: "(Frencais)", currency: "EUR" },
];
const { locale, setLocale } = useI18n();
const labelHandle = ref<any>(locale.value);
const _language = reactive<any>(languages);
const languageKey = ref(0);
const changeLanguage = (payload: any) => {
    setLocale(payload.value);
    languageKey.value++;
};
</script>
